import gmailIcon from '@lib/core/src/assets/third-party-logos/gmail.png';
import slackIcon from '@lib/core/src/assets/third-party-logos/slack.png';
import { Row as RowStyled } from '@lib/core/src/styledComponents/StyledComponents';
import palette from '@lib/core/src/theme/palette';
import { Box, Typography, Stack, Divider, styled } from '@mui/material';

import { TIME_RANGE_OPTIONS } from '@/components/AiWorkflow/formSchema';
import { FeaturedTemplateType } from '@/pages/JamiesCapabilities/featuredTemplatesData';

const CARD_BORDER_RADIUS = 16;

const CardContainer = styled(Box)({
  background: palette.white,
  border: `1px solid ${palette.grey[1200]}`,
  borderRadius: CARD_BORDER_RADIUS,
  width: 344,
  minWidth: 344,
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '0px 6px 8px -6px rgba(24, 39, 75, 0.12), 0px 8px 16px -6px rgba(24, 39, 75, 0.08)'
  }
});

export const Content = styled(Stack)({
  padding: '0 16px 32px 16px'
});

const ICON_SIZE = '6vh';

const TopIconBox = styled(Box)({
  width: ICON_SIZE,
  height: ICON_SIZE,
  borderRadius: 8,
  background: palette.white,
  border: `1px solid ${palette.grey[1200]}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 32
});

const TitleText = styled(Typography)({
  fontFamily: 'Reddit Sans',
  fontWeight: 500,
  fontSize: 16,
  color: palette.black
});

const DescriptionText = styled(Typography)({
  fontFamily: 'Reddit Sans',
  fontWeight: 500,
  fontSize: 12,
  color: palette.grey[500],
  marginTop: 8
});

const Label = styled(Typography)({
  fontFamily: 'Reddit Sans',
  fontWeight: 600,
  fontSize: 12,
  color: palette.black
});

const Value = styled(Typography)({
  fontFamily: 'Reddit Sans',
  fontWeight: 500,
  fontSize: 12,
  color: palette.grey[500]
});

const OutputIcon = styled('img')({
  width: 15,
  height: 15,
  marginRight: 8
});

const TitleRow = styled(RowStyled, {
  shouldForwardProp: prop => prop !== 'titleImage'
})<{ titleImage: string }>(({ titleImage }) => ({
  position: 'relative',
  overflow: 'hidden',
  padding: '1vh',
  height: '10vh',
  '&::before': {
    content: '""',
    position: 'absolute',
    borderTopLeftRadius: CARD_BORDER_RADIUS,
    borderTopRightRadius: CARD_BORDER_RADIUS,
    top: 0,
    left: 0,
    width: '100%',
    height: '50%',
    backgroundImage: `url(${titleImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center top',
    zIndex: 0
  },
  '> *': {
    position: 'relative',
    zIndex: 1
  }
}));

const Row = styled(RowStyled)({
  justifyContent: 'space-between',
  alignItems: 'flex-start'
});

const HalfWidthStack = styled(Stack)({
  width: '50%',
  gap: '1vh'
});

interface FeaturedCardProps {
  template: FeaturedTemplateType;
  onClick: () => void;
}

const FeaturedCard = ({ template, onClick }: FeaturedCardProps) => (
  <CardContainer onClick={onClick}>
    <TitleRow titleImage={template.image}>
      <TopIconBox>
        <img src={template.icon} alt={template.title} />
      </TopIconBox>
    </TitleRow>
    <Content>
      <Box>
        <TitleText>{template.title}</TitleText>
        <DescriptionText>{template.description}</DescriptionText>
      </Box>
      <Divider sx={{ my: 2, borderColor: palette.grey[1200] }} />
      <Row>
        <HalfWidthStack>
          <Label>Based on</Label>
          <Value>
            {TIME_RANGE_OPTIONS.find(option => option.value === template.timeRange)?.label}
          </Value>
        </HalfWidthStack>
        <HalfWidthStack>
          <Label>Output</Label>
          <Stack sx={{ gap: '12px' }}>
            {template.notifications?.slack && (
              <RowStyled>
                <OutputIcon src={slackIcon} alt="Slack" />
                <Value>Send Slack</Value>
              </RowStyled>
            )}
            {template.notifications?.email && (
              <RowStyled>
                <OutputIcon src={gmailIcon} alt="Gmail" />
                <Value>Send email</Value>
              </RowStyled>
            )}
          </Stack>
        </HalfWidthStack>
      </Row>
    </Content>
  </CardContainer>
);

export default FeaturedCard;
